import { DoctorStatus, type DoctorProfile } from '~/types/telemedicine'
import { doctorPortalLink, isRateLimited } from '~/utils/telemedicineDoctor'

type DoctorRef = Pick<DoctorProfile, 'id' | 'title' | 'full_name' | 'email' | 'status'>

export function useDoctorActions() {
    const toast = useToast()

    function name(doctor: DoctorRef) {
        return [doctor.title, doctor.full_name].filter(Boolean).join(' ')
    }

    function successTitle(doctor: DoctorRef, next: DoctorStatus) {
        if (next === DoctorStatus.published) return `${name(doctor)} is now live in the app`
        if (next === DoctorStatus.suspended) return `${name(doctor)} has been suspended`
        return doctor.status === DoctorStatus.suspended ? `${name(doctor)} reinstated to draft` : `${name(doctor)} unpublished`
    }

    async function changeStatus(doctor: DoctorRef, next: DoctorStatus): Promise<boolean> {
        try {
            await $fetch(`/api/telemedicine/doctors/${doctor.id}/status`, { method: 'POST', body: { status: next } })
            toast.add({ title: successTitle(doctor, next), icon: 'i-ph-check-circle-duotone', color: 'green' })
            return true
        } catch (e: any) {
            const blockers: string[] | undefined = e?.data?.data?.blockers
            toast.add({
                title: e?.data?.message || 'Could not update the doctor’s status',
                description: blockers?.join(' · '),
                icon: 'i-ph-warning-duotone',
                color: 'red',
            })
            return false
        }
    }

    const { copy } = useClipboard()

    async function copyPortalLink(email: string) {
        try {
            await copy(doctorPortalLink(window.location.origin, email))
            toast.add({ title: 'Portal sign-in link copied', icon: 'i-ph-link-duotone' })
        } catch {
            toast.add({ title: 'Could not copy the link', description: doctorPortalLink(window.location.origin, email), icon: 'i-ph-warning-duotone', color: 'red' })
        }
    }

    function portalLinkAction(email: string) {
        return { label: 'Copy portal sign-in link', click: () => copyPortalLink(email) }
    }

    async function resendAccessEmail(doctor: DoctorRef): Promise<string | null> {
        try {
            const { invited_at } = await $fetch<{ invited_at: string }>(`/api/telemedicine/doctors/${doctor.id}/invite`, { method: 'POST' })
            toast.add({
                title: `Access email sent to ${doctor.email}`,
                description: 'It contains a 6-digit code to set their password. Send them the portal sign-in link to enter it.',
                icon: 'i-ph-envelope-simple-duotone',
                timeout: 15000,
                actions: [portalLinkAction(doctor.email)],
            })
            return invited_at
        } catch (e: any) {
            const message: string | undefined = e?.data?.message?.replace(/^The access email could not be sent:\s*/, '')
            toast.add(isRateLimited(message)
                ? { title: 'Please wait a minute before resending', description: message, icon: 'i-ph-hourglass-medium-duotone', color: 'orange' }
                : { title: 'Could not send the access email', description: message, icon: 'i-ph-warning-duotone', color: 'red' })
            return null
        }
    }

    return { changeStatus, resendAccessEmail, copyPortalLink, portalLinkAction }
}
